import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmActionDialog } from '../../../components/design-system/admin';
import { BLOCK_REASONS, BlockReason, BlockSignupPayload } from '../../../common/models/admin-signups.model';
import { enumKey } from './signup-parts';

const LABEL = 'text-xs font-medium text-muted-foreground';

/**
 * "Block…" — the reason (shown to the vendor in plain words, in their
 * language), the admin's own message to them, an internal note they never
 * see, and whether to email them.
 */
export function BlockSignupDialog({
  open,
  onOpenChange,
  domain,
  busy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: string;
  busy?: boolean;
  onConfirm: (payload: BlockSignupPayload) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<BlockReason>('no_business');
  const [message, setMessage] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setReason('no_business');
    setMessage('');
    setNote('');
    setNotify(true);
  }, [open]);

  return (
    <ConfirmActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('ADMIN_SIGNUPS.BLOCK_DIALOG.TITLE', { domain: domain || t('ADMIN_SIGNUPS.TABLE.NO_DOMAIN') })}
      description={t('ADMIN_SIGNUPS.BLOCK_DIALOG.DESCRIPTION')}
      confirmLabel={t('ADMIN_SIGNUPS.BLOCK_DIALOG.CONFIRM')}
      destructive
      busy={busy}
      onConfirm={() =>
        onConfirm({
          reason,
          ...(message.trim() ? { message: message.trim() } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          notify,
        })
      }
    >
      <div className="grid gap-4" data-testid="block-dialog">
        <div className="grid gap-1.5">
          <Label htmlFor="block-reason" className={LABEL}>
            {t('ADMIN_SIGNUPS.BLOCK_DIALOG.REASON')}
          </Label>
          <Select value={reason} onValueChange={(v) => setReason(v as BlockReason)}>
            <SelectTrigger id="block-reason" className="w-full" aria-label={t('ADMIN_SIGNUPS.BLOCK_DIALOG.REASON')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BLOCK_REASONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`ADMIN_SIGNUPS.REASON.${enumKey(r)}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="block-message" className={LABEL}>
            {t('ADMIN_SIGNUPS.BLOCK_DIALOG.MESSAGE')}
          </Label>
          <Textarea id="block-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('ADMIN_SIGNUPS.BLOCK_DIALOG.MESSAGE_HINT')} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="block-note" className={LABEL}>
            {t('ADMIN_SIGNUPS.BLOCK_DIALOG.NOTE')}
          </Label>
          <Textarea id="block-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('ADMIN_SIGNUPS.BLOCK_DIALOG.NOTE_HINT')} />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="block-notify" checked={notify} onCheckedChange={(v) => setNotify(v === true)} />
          <Label htmlFor="block-notify" className="text-sm font-normal">
            {t('ADMIN_SIGNUPS.BLOCK_DIALOG.NOTIFY')}
          </Label>
        </div>
      </div>
    </ConfirmActionDialog>
  );
}
